import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, ArrowRight, Calendar } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";

export default function Booking() {
  return (
    <>
      <Navbar />

      <main id="main-content" className="flex-1 w-full relative">
        {/* HERO */}
        <section className="relative pt-32 pb-20 overflow-hidden" aria-label="Booking hero">
          <div className="absolute inset-0 bg-background z-0" aria-hidden="true" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:24px_24px] z-0" aria-hidden="true" />
          <div className="absolute top-1/3 left-1/3 w-[500px] h-[500px] bg-primary/15 rounded-full blur-[120px] -z-10 mix-blend-screen" aria-hidden="true" />

          <div className="max-w-3xl mx-auto px-4 sm:px-6 relative z-10 text-center">
            <Badge variant="outline" className="border-primary/30 text-primary px-4 py-1.5 backdrop-blur-sm bg-primary/5 uppercase tracking-wider text-xs mb-6" data-testid="badge-call-duration">
              <Calendar size={14} className="mr-2" aria-hidden="true" />
              15-Minute Call
            </Badge>
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-display font-bold leading-[1.1] tracking-tight mb-6" data-testid="text-booking-title">
              Growth Strategy Call
            </h1>
            <p className="text-lg sm:text-xl text-foreground/70 max-w-2xl mx-auto leading-relaxed mb-8" data-testid="text-booking-description">
              A focused 15-minute conversation for founders who want to break through stalled growth, improve execution, and build a more scalable business.
            </p>
            <Button size="lg" className="h-14 px-8 text-base font-semibold shadow-[0_0_40px_-10px_rgba(var(--primary),0.5)] transition-all hover:scale-105" asChild>
              <a href="#book" data-testid="link-booking-cta">
                Book Your Time Below <ArrowRight className="ml-2" size={18} aria-hidden="true" />
              </a>
            </Button>
          </div>
        </section>

        {/* WHAT THIS CALL IS FOR */}
        <section className="py-20 bg-secondary/20 border-y border-white/5" aria-label="What this call is for">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px w-8 bg-primary" aria-hidden="true" />
              <h2 className="text-primary font-medium tracking-wide uppercase text-sm" data-testid="text-section-call-for">What This Call Is For</h2>
            </div>
            <p className="text-lg text-foreground/70 mb-8">
              This call is designed for founders and CEOs who are facing one or more of the following:
            </p>
            <ul className="space-y-3" role="list" aria-label="Challenges this call addresses">
              {[
                "Growth has stalled or plateaued",
                "Operations are becoming harder to scale",
                "Product is live, but go-to-market performance is underwhelming",
                "Strategy feels clear, but execution is not producing the right outcomes",
                "The business needs stronger focus, sharper priorities, and better operating leverage",
              ].map((item, i) => (
                <li key={i} role="listitem" className="flex items-start gap-3 p-4 rounded-xl border border-white/5 bg-card/50" data-testid={`text-challenge-${i}`}>
                  <CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" aria-hidden="true" />
                  <p className="text-sm text-foreground/80">{item}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* WHAT YOU CAN EXPECT */}
        <section className="py-20" aria-label="What you can expect">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px w-8 bg-primary" aria-hidden="true" />
              <h2 className="text-primary font-medium tracking-wide uppercase text-sm" data-testid="text-section-expect">What You Can Expect</h2>
            </div>
            <p className="text-lg text-foreground/70 mb-8">
              In this call, we will briefly look at:
            </p>
            <ul className="space-y-3" role="list" aria-label="What to expect from the call">
              {[
                "Where growth may be getting stuck",
                "What is creating friction in execution",
                "Which next moves may have the highest leverage",
                "Whether ongoing strategic advisory support would make sense",
              ].map((item, i) => (
                <li key={i} role="listitem" className="flex items-start gap-3 p-4 rounded-xl border border-white/5 bg-card/50" data-testid={`text-expect-${i}`}>
                  <CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" aria-hidden="true" />
                  <p className="text-sm text-foreground/80">{item}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* WHAT YOU WILL LEAVE WITH */}
        <section className="py-20 bg-secondary/20 border-y border-white/5" aria-label="What you will leave with">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px w-8 bg-primary" aria-hidden="true" />
              <h2 className="text-primary font-medium tracking-wide uppercase text-sm" data-testid="text-section-leave-with">What You Will Leave With</h2>
            </div>
            <p className="text-lg text-foreground/70 mb-8">
              By the end of the conversation, you should have:
            </p>
            <ul className="space-y-3" role="list" aria-label="Outcomes from the call">
              {[
                "A clearer view of the likely bottleneck",
                "A stronger sense of what to prioritize next",
                "An understanding of whether there is a fit for working together",
              ].map((item, i) => (
                <li key={i} role="listitem" className="flex items-start gap-3 p-4 rounded-xl border border-white/5 bg-card/50" data-testid={`text-outcome-${i}`}>
                  <CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" aria-hidden="true" />
                  <p className="text-sm text-foreground/80">{item}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* FIT STATEMENT + SCHEDULER */}
        <section id="book" className="py-20" aria-label="Book your call">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <Card className="bg-card border-white/10 p-8 md:p-10 text-center mb-12" data-testid="text-fit-statement">
              <p className="text-foreground/70 leading-relaxed">
                This call is best suited to Founders and CEOs of software & SaaS companies with a live product who want clearer strategy, stronger execution, and better scalability.
              </p>
            </Card>

            <div className="mb-10">
              <div className="flex items-center justify-center gap-3 mb-8">
                <div className="h-px w-8 bg-primary" aria-hidden="true" />
                <h2 className="text-primary font-medium tracking-wide uppercase text-sm">Book Your Time</h2>
                <div className="h-px w-8 bg-primary" aria-hidden="true" />
              </div>
              <div className="rounded-xl border border-white/10 overflow-hidden bg-white" data-testid="booking-calendar-embed">
                <iframe
                  src="https://calendar.google.com/calendar/appointments/schedules/AcZssZ0eYB6IQ29sIw69N5Rszvrwm5yTQKoQ4stwUBUa0988tRwfmy-3Ehxq9LwgeMNjJMIiQqvBGBCj?gv=true"
                  style={{ border: 0 }}
                  width="100%"
                  height="600"
                  frameBorder="0"
                  title="Book a Growth Strategy Call with Matt Rebeiro"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
